import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft,
  ArrowRight,
  GraduationCap,
  Scale,
  Network,
  Shield,
  Award,
  Briefcase,
  CheckCircle2,
  Calendar,
  MessageCircle,
  FileText,
  ExternalLink,
  Clock,
  Users,
  BookOpen,
  Video,
  Download,
  Phone,
  Star
} from 'lucide-react';

// Datos detallados de cada beneficio
const beneficiosDetalle = {
  formacion: {
    id: 'formacion',
    icon: GraduationCap,
    title: 'Formación Continua',
    subtitle: 'Capacítate constantemente y mantente actualizado',
    heroImage: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1200&h=500&fit=crop',
    description: 'Como afiliado UPIM tienes acceso a un programa completo de formación continua diseñado para mantenerte actualizado en las últimas tendencias, regulaciones y mejores prácticas del sector inmobiliario mexicano.',
    features: [
      {
        icon: Video,
        title: 'Master Classes Quincenales',
        description: 'Sesiones en vivo cada dos semanas con expertos del sector sobre temas relevantes y actuales.'
      },
      {
        icon: Award,
        title: 'Certificación CONOCER EC0110.02',
        description: 'Accede a la certificación oficial con descuento especial para afiliados ($3,479 vs $4,639).'
      },
      {
        icon: BookOpen,
        title: 'Academia UPIM',
        description: 'Plataforma de e-learning con cursos, webinars grabados y material de estudio disponible 24/7.'
      },
      {
        icon: Calendar,
        title: 'Eventos Especializados',
        description: 'Acceso preferencial a Real Estate Technology y otros eventos formativos del sector.'
      }
    ],
    stats: [
      { value: '24+', label: 'Master Classes al año' },
      { value: '100+', label: 'Horas de contenido' },
      { value: '15+', label: 'Expertos certificados' },
      { value: '4.8', label: 'Calificación promedio' }
    ],
    cta: {
      primary: { text: 'Ver Academia', link: '/academia' },
      secondary: { text: 'Ver Calendario', link: '/calendario' }
    }
  },
  asesoria: {
    id: 'asesoria',
    icon: Scale,
    title: 'Asesoría Integral',
    subtitle: 'Soporte profesional sin costo adicional',
    heroImage: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=500&fit=crop',
    description: 'Accede a un equipo de expertos en materia jurídica, financiera y contable que te apoyarán en tus operaciones inmobiliarias sin costo adicional a tu membresía.',
    features: [
      {
        icon: Scale,
        title: 'Asesoría Jurídica',
        description: 'Resuelve dudas sobre arrendamiento, compra-venta, contratos y aspectos legales a través de nuestro chat especializado.'
      },
      {
        icon: Award,
        title: 'Asesoría Financiera',
        description: 'Orientación gratuita en temas de créditos hipotecarios, INFONAVIT, FOVISSSTE y opciones de financiamiento para tus clientes.'
      },
      {
        icon: FileText,
        title: 'Asesoría Contable y Fiscal',
        description: 'Apoyo sin costo en declaraciones, facturación, régimen fiscal y obligaciones tributarias del asesor inmobiliario.'
      },
      {
        icon: MessageCircle,
        title: 'Chat de Consultas',
        description: 'Grupo exclusivo de WhatsApp donde puedes hacer consultas directas a los especialistas.'
      }
    ],
    stats: [
      { value: '500+', label: 'Consultas mensuales' },
      { value: '24h', label: 'Tiempo de respuesta' },
      { value: '3', label: 'Áreas de asesoría' },
      { value: '$0', label: 'Costo adicional' }
    ],
    cta: {
      primary: { text: 'Unirse al Chat Jurídico', link: 'https://chat.whatsapp.com/Df7VRE2j5rH3mUdw3qIxJw', external: true },
      secondary: { text: 'Ver Beneficios', link: '/beneficios' }
    }
  },
  networking: {
    id: 'networking',
    icon: Network,
    title: 'Red de Networking',
    subtitle: 'Conecta con más de 2,000 profesionales',
    heroImage: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&h=500&fit=crop',
    description: 'Forma parte de la red de networking inmobiliario más grande de México. Conecta con asesores de todas las zonas del país para compartir propiedades, referir clientes y generar más negocios.',
    features: [
      {
        icon: MessageCircle,
        title: 'Grupos de WhatsApp por Zona',
        description: 'Acceso a grupos segmentados geográficamente: Zona Oriente, Poniente, Centro, Norte, Sur, Edomex y Foráneos.'
      },
      {
        icon: Users,
        title: 'Comunidad Activa',
        description: 'Más de 2,000 profesionales activos compartiendo oportunidades de negocio diariamente.'
      },
      {
        icon: Calendar,
        title: 'Eventos de Networking',
        description: 'Reuniones presenciales y virtuales para conocer a otros afiliados y generar sinergias.'
      },
      {
        icon: Star,
        title: 'Comité de Mujeres',
        description: 'Iniciativa especial para fortalecer el networking entre profesionales mujeres del sector.'
      }
    ],
    stats: [
      { value: '2,150+', label: 'Afiliados activos' },
      { value: '9', label: 'Grupos por zona' },
      { value: '32', label: 'Estados' },
      { value: '1000s', label: 'Oportunidades compartidas' }
    ],
    cta: {
      primary: { text: 'Ver Grupos WhatsApp', link: '/beneficios' },
      secondary: { text: 'Afiliarse Ahora', link: '/afiliacion' }
    }
  },
  blindaje: {
    id: 'blindaje',
    icon: Shield,
    title: 'Blindaje Jurídico',
    subtitle: 'Protege tus operaciones inmobiliarias',
    heroImage: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=500&fit=crop',
    description: 'Accede a contratos modelo, protocolos y documentos que cumplen con la NOM-247-SE2021 para realizar operaciones de compra-venta seguras y profesionales.',
    features: [
      {
        icon: FileText,
        title: 'Contratos Modelo NOM-247',
        description: 'Contratos de compra-venta y arrendamiento actualizados que cumplen con la normativa vigente.'
      },
      {
        icon: Download,
        title: 'Protocolos Descargables',
        description: 'Material completo que solicita la norma para tus operaciones: fichas técnicas, avisos de privacidad, y más.'
      },
      {
        icon: Shield,
        title: 'Dictamen Estructural',
        description: 'Acceso a dictámenes emitidos por D.R.O. según la NOM-247-SE2021 a precios preferenciales.'
      },
      {
        icon: BookOpen,
        title: 'Biblioteca de Formatos',
        description: 'Templates de publicidad, formatos de captación, cartas compromiso y más documentos profesionales.'
      }
    ],
    stats: [
      { value: '50+', label: 'Documentos disponibles' },
      { value: 'NOM-247', label: 'Cumplimiento normativo' },
      { value: '100%', label: 'Actualizados' },
      { value: '24/7', label: 'Acceso ilimitado' }
    ],
    cta: {
      primary: { text: 'Ver Recursos', link: '/recursos' },
      secondary: { text: 'Ver Beneficios', link: '/beneficios' }
    }
  },
  descuentos: {
    id: 'descuentos',
    icon: Award,
    title: 'Descuentos Exclusivos',
    subtitle: 'Ahorra en servicios profesionales',
    heroImage: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=500&fit=crop',
    description: 'Como afiliado UPIM accedes a descuentos y precios preferenciales con nuestra red de proveedores certificados: avalúos, notarías, seguros, marketing y más.',
    features: [
      {
        icon: FileText,
        title: 'Avalúos Preferenciales',
        description: 'Avalúos certificados desde $2,500 a $3,500 con nuestros proveedores autorizados.'
      },
      {
        icon: Award,
        title: '38% en Mercado Libre',
        description: 'Descuento especial en paquetes de publicación de propiedades en Inmuebles24 y Mercado Libre.'
      },
      {
        icon: Star,
        title: 'Morgana - 0.5% Comisión',
        description: 'Comisión preferencial por créditos colocados a través de la plataforma Morgana.'
      },
      {
        icon: Shield,
        title: 'Seguros Colectivos',
        description: 'Acceso a seguros de gastos médicos mayores a precios especiales para asesores.'
      }
    ],
    stats: [
      { value: '48+', label: 'Proveedores certificados' },
      { value: '15-40%', label: 'Descuentos promedio' },
      { value: '$10k+', label: 'Ahorro potencial anual' },
      { value: '6', label: 'Categorías de descuento' }
    ],
    cta: {
      primary: { text: 'Ver Proveedores', link: '/proveedores' },
      secondary: { text: 'Afiliarse Ahora', link: '/afiliacion' }
    }
  },
  herramientas: {
    id: 'herramientas',
    icon: Briefcase,
    title: 'Herramientas Profesionales',
    subtitle: 'Todo lo que necesitas para operar',
    heroImage: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=500&fit=crop',
    description: 'Accede a una biblioteca digital completa con templates, contratos, formatos, grabaciones de Master Classes y ebooks diseñados para profesionalizar tu operación inmobiliaria.',
    features: [
      {
        icon: Download,
        title: 'Biblioteca Digital',
        description: 'Más de 50 recursos descargables: templates de publicidad, contratos, formatos y material de capacitación.'
      },
      {
        icon: Video,
        title: 'Grabaciones de Master Classes',
        description: 'Acceso ilimitado a todas las grabaciones de sesiones anteriores organizadas por tema.'
      },
      {
        icon: BookOpen,
        title: 'Ebooks y Guías',
        description: 'Material educativo exclusivo sobre ventas, marketing, aspectos legales y tendencias del mercado.'
      },
      {
        icon: Star,
        title: 'Credencial Digital con QR',
        description: 'Credencial profesional digital que valida tu afiliación ante clientes y colegas.'
      }
    ],
    stats: [
      { value: '50+', label: 'Recursos descargables' },
      { value: '100+', label: 'Videos disponibles' },
      { value: '10+', label: 'Ebooks y guías' },
      { value: '24/7', label: 'Acceso ilimitado' }
    ],
    cta: {
      primary: { text: 'Ver Recursos', link: '/recursos' },
      secondary: { text: 'Ir a Academia', link: '/academia' }
    }
  }
};

const BeneficioDetallePage = () => {
  const { beneficioId } = useParams();
  const beneficio = beneficiosDetalle[beneficioId];

  // Si no existe el beneficio, redirigir a beneficios
  if (!beneficio) {
    return <Navigate to="/beneficios" replace />;
  }

  const IconComponent = beneficio.icon;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gray-900 text-white overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${beneficio.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/90 to-gray-900/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <Link 
            to="/beneficios" 
            className="inline-flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
            data-testid="back-to-benefits-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a beneficios
          </Link>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="h-16 w-16 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
              <IconComponent className="h-8 w-8 text-white" />
            </div>
            <div>
              <p className="text-gray-400 text-sm uppercase tracking-wider">{beneficio.subtitle}</p>
              <h1 className="text-4xl lg:text-5xl font-bold" data-testid="benefit-title">{beneficio.title}</h1>
            </div>
          </div>
          
          <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
            {beneficio.description}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {beneficio.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ¿Qué incluye este beneficio?
            </h2>
            <p className="text-gray-600">
              Conoce todo lo que obtienes con {beneficio.title.toLowerCase()} como afiliado UPIM.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {beneficio.features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <Card key={index} className="bg-white border border-gray-100 hover:shadow-lg transition-all group">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 transition-colors">
                        <FeatureIcon className="h-6 w-6 text-gray-700 group-hover:text-white transition-colors" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other Benefits */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explora otros beneficios
            </h2>
            <p className="text-gray-600">
              Como afiliado UPIM, tienes acceso a todos estos beneficios adicionales.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {Object.values(beneficiosDetalle)
              .filter(b => b.id !== beneficioId)
              .slice(0, 3)
              .map((otherBenefit) => {
                const OtherIcon = otherBenefit.icon;
                return (
                  <Link
                    key={otherBenefit.id}
                    to={`/beneficios/${otherBenefit.id}`}
                    className="p-6 rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all group block"
                    data-testid={`other-benefit-${otherBenefit.id}`}
                  >
                    <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center mb-4 group-hover:bg-gray-900 transition-colors">
                      <OtherIcon className="h-6 w-6 text-gray-700 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{otherBenefit.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{otherBenefit.subtitle}</p>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-6 text-gray-500" />
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Accede a {beneficio.title}
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Este y todos los beneficios UPIM están disponibles desde $3,500 anuales.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {beneficio.cta.primary.external ? (
              <Button 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
                asChild
              >
                <a href={beneficio.cta.primary.link} target="_blank" rel="noopener noreferrer">
                  {beneficio.cta.primary.text}
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </Button>
            ) : (
              <Button 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
                asChild
                data-testid="benefit-cta-primary"
              >
                <Link to={beneficio.cta.primary.link}>
                  {beneficio.cta.primary.text}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            )}
            <Button 
              size="lg" 
              variant="outline" 
              className="border-gray-600 text-white hover:bg-gray-800 px-8 h-12"
              asChild
              data-testid="benefit-cta-secondary"
            >
              <Link to={beneficio.cta.secondary.link}>
                {beneficio.cta.secondary.text}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BeneficioDetallePage;
